import {
    createColumns,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { formatCurrency, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MonthlyAllocatedRevenue, PerformanceResult } from '../../types'
import { RevenueType, ViewMode } from './index'

/** Sum selected revenue types from a breakdown */
const sumSelectedRevenue = (
    data: {
        renewal_revenue: number
        new_sale_revenue: number
        upsell_revenue: number
    },
    types: RevenueType[]
): number => {
    let total = 0
    if (types.includes('renewal')) total += data.renewal_revenue || 0
    if (types.includes('new_sale')) total += data.new_sale_revenue || 0
    if (types.includes('upsell')) total += data.upsell_revenue || 0
    return total
}

/** Progress bar cell: fills entire cell, gray bg = target, colored fill = actual % */
const ProgressCell = ({
    value,
    monthlyTarget,
}: {
    value: number
    monthlyTarget: number
}) => {
    const percent =
        monthlyTarget > 0 ? Math.min((value / monthlyTarget) * 100, 100) : 0
    const overAchieved = monthlyTarget > 0 && value > monthlyTarget

    return (
        <div className="absolute inset-0 bg-gray-100">
            <div
                className={`absolute inset-y-0 left-0 transition-all ${
                    overAchieved ? 'bg-emerald-400/60' : 'bg-blue-400/50'
                }`}
                style={{ width: `${percent}%` }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-gray-700">
                {value > 0
                    ? `${formatNumeral(Number(((value / monthlyTarget) * 100).toFixed(0)))}%`
                    : ''}
            </span>
        </div>
    )
}

interface UsePerformanceTableColumnsProps {
    selectedRevenueTypes: RevenueType[]
    getComparisonEmployee: (employeeId: number) => PerformanceResult | undefined
    renderChangeIndicator: (
        current: number,
        previous: number | undefined
    ) => React.ReactNode
    monthColumns: string[]
    monthlyBreakdownMap: Map<number, MonthlyAllocatedRevenue>
    viewMode: ViewMode
}

export function usePerformanceTableColumns({
    selectedRevenueTypes,
    getComparisonEmployee,
    renderChangeIndicator,
    monthColumns,
    monthlyBreakdownMap,
    viewMode,
}: UsePerformanceTableColumnsProps): DataTableColumn<PerformanceResult>[] {
    const { t } = useTranslation()

    return useMemo(() => {
        const isProgress = viewMode === 'progress'
        const monthCount = monthColumns.length || 1

        const cols: any[] = [
            {
                key: 'employee_name',
                title: t('employees.name'),
                width: 180,
                headerClassName: 'text-left',
                cellClassName: 'font-medium',
            },
            {
                key: 'target_amount',
                title: t('employees.performance.commitment'),
                width: 140,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value: number, row: PerformanceResult) => {
                    const compEmp = getComparisonEmployee(row.employee_id)
                    return (
                        <span>
                            {formatCurrency(value)}
                            {renderChangeIndicator(
                                value,
                                compEmp?.target_amount
                            )}
                        </span>
                    )
                },
            },
        ]

        // Dynamic monthly columns
        monthColumns.forEach((month) => {
            cols.push({
                key: `month_${month}`,
                title: `T${dayjs(month).format('M')}`,
                width: isProgress ? 130 : 110,
                headerClassName: isProgress ? 'text-center' : 'text-right',
                cellClassName: isProgress ? 'relative !p-0' : 'text-right',
                render: (_: unknown, row: PerformanceResult) => {
                    const empData = monthlyBreakdownMap.get(row.employee_id)
                    const breakdown = empData?.monthly_breakdown?.find(
                        (b) => b.revenue_month === month
                    )
                    const value = breakdown
                        ? sumSelectedRevenue(breakdown, selectedRevenueTypes)
                        : 0

                    if (isProgress) {
                        const monthlyTarget = row.target_amount / monthCount
                        return (
                            <ProgressCell
                                value={value}
                                monthlyTarget={monthlyTarget}
                            />
                        )
                    }

                    return formatCurrency(value)
                },
            })
        })

        // Total + breakdown + achievement columns
        cols.push(
            {
                key: 'actual_revenue',
                title: t('employees.performance.actual_achieved'),
                width: 140,
                headerClassName: 'text-right bg-blue-50',
                cellClassName: 'text-right font-semibold bg-blue-50/50',
                render: (_: unknown, row: PerformanceResult) => {
                    let selectedRevenue = 0
                    if (selectedRevenueTypes.includes('renewal')) {
                        selectedRevenue += row.renewal_revenue || 0
                    }
                    if (selectedRevenueTypes.includes('new_sale')) {
                        selectedRevenue += row.new_sale_revenue || 0
                    }
                    if (selectedRevenueTypes.includes('upsell')) {
                        selectedRevenue += row.upsell_revenue || 0
                    }
                    const compEmp = getComparisonEmployee(row.employee_id)
                    return (
                        <span>
                            {formatCurrency(selectedRevenue)}
                            {renderChangeIndicator(
                                selectedRevenue,
                                compEmp?.actual_revenue
                            )}
                        </span>
                    )
                },
            },
            {
                key: 'renewal_revenue',
                title: (
                    <span className="flex items-center gap-1.5 justify-end">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        {t('employees.performance.renewal')}
                    </span>
                ),
                width: 130,
                headerClassName: 'text-right',
                cellClassName: 'text-right text-gray-700',
                render: (value: number, row: PerformanceResult) => {
                    const compEmp = getComparisonEmployee(row.employee_id)
                    return (
                        <span>
                            {formatCurrency(value || 0)}
                            {renderChangeIndicator(
                                value || 0,
                                compEmp?.renewal_revenue
                            )}
                        </span>
                    )
                },
            },
            {
                key: 'new_sale_revenue',
                title: (
                    <span className="flex items-center gap-1.5 justify-end">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        {t('employees.performance.new_sale')}
                    </span>
                ),
                width: 130,
                headerClassName: 'text-right',
                cellClassName: 'text-right text-gray-700',
                render: (value: number, row: PerformanceResult) => {
                    const compEmp = getComparisonEmployee(row.employee_id)
                    return (
                        <span>
                            {formatCurrency(value || 0)}
                            {renderChangeIndicator(
                                value || 0,
                                compEmp?.new_sale_revenue
                            )}
                        </span>
                    )
                },
            },
            {
                key: 'upsell_revenue',
                title: (
                    <span className="flex items-center gap-1.5 justify-end">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        {t('employees.performance.upsell')}
                    </span>
                ),
                width: 130,
                headerClassName: 'text-right',
                cellClassName: 'text-right text-gray-700',
                render: (value: number, row: PerformanceResult) => {
                    const compEmp = getComparisonEmployee(row.employee_id)
                    return (
                        <span>
                            {formatCurrency(value || 0)}
                            {renderChangeIndicator(
                                value || 0,
                                compEmp?.upsell_revenue
                            )}
                        </span>
                    )
                },
            },
            {
                key: 'achievement_percentage',
                title: t('employees.performance.achievement_percentage'),
                width: 100,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (_: unknown, row: PerformanceResult) => {
                    let selectedRevenue = 0
                    if (selectedRevenueTypes.includes('renewal')) {
                        selectedRevenue += row.renewal_revenue || 0
                    }
                    if (selectedRevenueTypes.includes('new_sale')) {
                        selectedRevenue += row.new_sale_revenue || 0
                    }
                    if (selectedRevenueTypes.includes('upsell')) {
                        selectedRevenue += row.upsell_revenue || 0
                    }
                    const achievementPercentage =
                        row.target_amount > 0
                            ? (selectedRevenue / row.target_amount) * 100
                            : 0

                    return (
                        <span
                            className={`font-semibold ${
                                achievementPercentage >= 100
                                    ? 'text-green-600'
                                    : achievementPercentage >= 80
                                      ? 'text-yellow-600'
                                      : 'text-red-600'
                            }`}
                        >
                            {formatNumeral(
                                Number(achievementPercentage.toFixed(2))
                            )}
                            %
                        </span>
                    )
                },
            }
        )

        return createColumns<PerformanceResult>(cols)
    }, [
        t,
        selectedRevenueTypes,
        getComparisonEmployee,
        renderChangeIndicator,
        monthColumns,
        monthlyBreakdownMap,
        viewMode,
    ])
}
